import React, { useState, useEffect } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { OrdersService, type PedidoResponse } from '../../services/orders/orders.service';
import { webSocketService } from '../../services/websocket/websocket.service';
import { OrderChat } from '../chat/OrderChat';
import {
  Package,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  CreditCard,
  MapPin,
  Calendar,
  ArrowLeft,
} from 'lucide-react';

export const estadosPedido: Record<string, {
  label: string;
  color: string;
  bg: string;
  border: string;
  icono: React.ReactNode;
  descripcion: string;
}> = {
  'PENDING_PAYMENT': {
    label: 'Pendiente de pago',
    color: 'text-yellow-700',
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    icono: <Clock className="w-6 h-6" />,
    descripcion: 'Tu pedido está esperando confirmación de pago'
  },
  'PAID': {
    label: 'Pagado',
    color: 'text-blue-700',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icono: <CreditCard className="w-6 h-6" />,
    descripcion: 'Hemos recibido tu pago y estamos preparando tu pedido'
  },
  'PROCESSING': {
    label: 'En preparación',
    color: 'text-purple-700',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    icono: <Package className="w-6 h-6" />,
    descripcion: 'Tu pedido está siendo preparado en nuestra farmacia'
  },
  'SHIPPED': {
    label: 'En camino',
    color: 'text-indigo-700',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    icono: <Truck className="w-6 h-6" />,
    descripcion: 'Tu pedido está en ruta de entrega'
  },
  'DELIVERED': {
    label: 'Entregado',
    color: 'text-green-700',
    bg: 'bg-green-50',
    border: 'border-green-200',
    icono: <CheckCircle className="w-6 h-6" />,
    descripcion: 'Tu pedido ha sido entregado exitosamente'
  },
  'CANCELLED': {
    label: 'Cancelado',
    color: 'text-red-700',
    bg: 'bg-red-50',
    border: 'border-red-200',
    icono: <XCircle className="w-6 h-6" />,
    descripcion: 'El pedido ha sido cancelado'
  },
};

const estadosTimeline = ['PENDING_PAYMENT', 'PAID', 'PROCESSING', 'SHIPPED', 'DELIVERED'];

const getEstadoIndex = (estado: string) => estadosTimeline.indexOf(estado);

interface PedidoDetalleProps {
  pedido: PedidoResponse;
  onPedidoChange?: (pedido: PedidoResponse) => void;
  headerAction?: React.ReactNode;
}

export const PedidoDetalle: React.FC<PedidoDetalleProps> = ({
  pedido: initialPedido,
  onPedidoChange,
  headerAction,
}) => {
  const { theme } = useTheme();
  const [pedido, setPedido] = useState<PedidoResponse>(initialPedido);

  // Sincronizar si cambia la prop externa
  useEffect(() => {
    setPedido(initialPedido);
  }, [initialPedido]);

  const formatFecha = (fecha: string) => {
    return new Date(fecha).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString('es-CO')}`;
  };

  // Notificación de cambio de estado
  const [statusChangeNotification, setStatusChangeNotification] = useState<{
    show: boolean;
    mensaje: string;
  }>({ show: false, mensaje: '' });

  // WebSocket para actualizaciones en tiempo real
  useEffect(() => {
    if (!webSocketService.isConnected()) {
      webSocketService.connect().catch(() => {
        // Silenciar errores de conexión
      });
    }

    const handleStatusChange = (payload: any) => {
      const { pedidoId, estado } = payload;
      if (pedido && pedido.id === pedidoId) {
        const estadoLabel = estadosPedido[estado]?.label || estado;
        setStatusChangeNotification({
          show: true,
          mensaje: `¡Tu pedido ahora está: ${estadoLabel}!`,
        });
        setTimeout(() => {
          setStatusChangeNotification({ show: false, mensaje: '' });
        }, 5000);

        const updated = { ...pedido, estado };
        setPedido(updated);
        onPedidoChange?.(updated);
      }
    };

    (webSocketService as any).addEventListener('order_status_change', handleStatusChange);

    return () => {
      (webSocketService as any).removeEventListener('order_status_change', handleStatusChange);
    };
  }, [pedido, onPedidoChange]);

  // Polling automático cada 10 segundos
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const response: any = await OrdersService.getByNumber(pedido.numeroPedido);
        if (response.success && response.data.estado !== pedido.estado) {
          const nuevoEstado = response.data.estado;
          const estadoLabel = estadosPedido[nuevoEstado]?.label || nuevoEstado;

          const updated = response.data;
          setPedido(updated);
          onPedidoChange?.(updated);

          setStatusChangeNotification({
            show: true,
            mensaje: `¡Tu pedido ahora está: ${estadoLabel}!`,
          });

          setTimeout(() => {
            setStatusChangeNotification({ show: false, mensaje: '' });
          }, 5000);
        }
      } catch (error) {
        // Silenciar errores de polling
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [pedido, onPedidoChange]);

  return (
    <div className="space-y-4">
      {/* Notificación de cambio de estado en tiempo real */}
      {statusChangeNotification.show && (
        <div className={`rounded-2xl shadow-lg p-4 animate-pulse ${
          theme === 'dark' ? 'bg-emerald-900/50 border border-emerald-700' : 'bg-emerald-50 border border-emerald-200'
        }`}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-emerald-500 text-white">
              <CheckCircle className="w-5 h-5" />
            </div>
            <p className={`font-medium ${theme === 'dark' ? 'text-emerald-300' : 'text-emerald-800'}`}>
              {statusChangeNotification.mensaje}
            </p>
            <button
              onClick={() => setStatusChangeNotification({ show: false, mensaje: '' })}
              className={`ml-auto p-1 rounded hover:bg-emerald-200 ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'}`}
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      <div className={`grid grid-cols-1 gap-4 ${pedido.envio.direccion !== 'Venta en local físico' ? 'xl:grid-cols-3' : 'max-w-4xl mx-auto'}`}>
        {/* Columna izquierda - Detalles del pedido */}
        <div className={pedido.envio.direccion !== 'Venta en local físico' ? 'xl:col-span-2 space-y-4' : 'space-y-4'}>
          {/* Header del pedido */}
          <div className={`rounded-xl shadow-md p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {headerAction}
              </div>
              <div className={`flex items-center gap-1.5 text-xs ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'}`}>
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className="hidden sm:inline">En tiempo real</span>
              </div>
            </div>

            {/* Estado actual */}
            {(() => {
              const estado = estadosPedido[pedido.estado];
              return (
                <div className={`flex flex-row items-center gap-3 p-3 rounded-lg ${estado?.bg} ${estado?.border} border`}>
                  <div className={`p-2 rounded-full ${estado?.bg} ${estado?.color}`}>
                    {estado?.icono}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className={`text-lg font-bold ${estado?.color} truncate`}>
                      {estado?.label}
                    </h2>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} truncate`}>
                      {estado?.descripcion}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-[10px] ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Total
                    </p>
                    <p className={`text-xl font-bold ${estado?.color}`}>
                      {formatCurrency(pedido.totales.total)}
                    </p>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Timeline visual */}
          {!['CANCELLED'].includes(pedido.estado) && (
            <div className={`rounded-xl shadow-md p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className={`font-semibold mb-4 text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Progreso
              </h3>
              <div className="relative">
                {/* Línea de progreso */}
                <div className={`absolute top-4 left-0 right-0 h-1 rounded ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`}>
                  <div
                    className="h-full bg-emerald-500 rounded transition-all duration-500"
                    style={{
                      width: `${Math.min((getEstadoIndex(pedido.estado) / (estadosTimeline.length - 1)) * 100, 100)}%`
                    }}
                  />
                </div>

                {/* Estados */}
                <div className="relative flex justify-between">
                  {estadosTimeline.map((estadoKey, index) => {
                    const estado = estadosPedido[estadoKey];
                    const currentIndex = getEstadoIndex(pedido.estado);
                    const isActive = index <= currentIndex;
                    const isCurrent = index === currentIndex;

                    return (
                      <div key={estadoKey} className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 transition-all ${
                          isCurrent
                            ? 'bg-emerald-500 text-white ring-4 ring-emerald-200'
                            : isActive
                              ? 'bg-emerald-500 text-white'
                              : theme === 'dark'
                                ? 'bg-gray-700 text-gray-500'
                                : 'bg-gray-200 text-gray-400'
                        }`}>
                          {isActive ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : (
                            <span className="text-sm">{index + 1}</span>
                          )}
                        </div>
                        <span className={`text-xs text-center max-w-[80px] ${
                          isActive
                            ? theme === 'dark' ? 'text-white font-medium' : 'text-gray-900 font-medium'
                            : theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          {estado?.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Detalles del pedido - 3 columnas en pantallas grandes */}
          <div className={`rounded-xl shadow-md p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
            <h3 className={`font-semibold mb-3 text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Detalles del pedido
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Información general */}
              <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <h4 className={`font-medium mb-2 text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Información
                </h4>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Número:</span>
                    <span className={theme === 'dark' ? 'text-white font-medium' : 'text-gray-900 font-medium'}>
                      {pedido.numeroPedido}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Fecha:</span>
                    <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                      {formatFecha(pedido.fechas.creadoEn)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Cliente:</span>
                    <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                      {pedido.cliente.nombre}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Email:</span>
                    <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                      {pedido.cliente.email}
                    </span>
                  </div>
                </div>
              </div>

              {/* Dirección de envío */}
              <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <h4 className={`font-medium mb-2 flex items-center gap-1 text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  <MapPin className="w-3 h-3" />
                  Envío
                </h4>
                <div className={`space-y-0.5 text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  <p>{pedido.envio.direccion}</p>
                  <p>{pedido.envio.barrio}, {pedido.envio.ciudad}</p>
                  {pedido.envio.indicaciones && (
                    <p className={`text-xs italic mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                      "{pedido.envio.indicaciones}"
                    </p>
                  )}
                </div>
              </div>

              {/* Totales */}
              <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <h4 className={`font-medium mb-2 text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Pago
                </h4>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Subtotal:</span>
                    <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                      {formatCurrency(pedido.totales.subtotal)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Envío:</span>
                    <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                      {pedido.totales.envio === 0 ? 'Gratis' : formatCurrency(pedido.totales.envio)}
                    </span>
                  </div>
                  {pedido.totales.descuento > 0 && (
                    <div className="flex justify-between">
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Dto:</span>
                      <span className="text-green-600">
                        -{formatCurrency(pedido.totales.descuento)}
                      </span>
                    </div>
                  )}
                  <div className="pt-1 border-t border-gray-200 dark:border-gray-600">
                    <div className="flex justify-between font-bold">
                      <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>Total:</span>
                      <span className="text-emerald-600 text-base">
                        {formatCurrency(pedido.totales.total)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Productos - tabla más ancha */}
            <div className="mt-4">
              <h4 className={`font-medium mb-2 text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Productos ({pedido.items.length})
              </h4>
              <div className={`rounded-lg overflow-hidden border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <table className="w-full text-xs">
                  <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}>
                    <tr>
                      <th className={`px-3 py-2 text-left font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        Producto
                      </th>
                      <th className={`px-3 py-2 text-center font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        Cant.
                      </th>
                      <th className={`px-3 py-2 text-right font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        Precio
                      </th>
                      <th className={`px-3 py-2 text-right font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        Subtotal
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {pedido.items.map((item) => (
                      <tr key={item.id}>
                        <td className={`px-3 py-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          {item.nombreProducto}
                        </td>
                        <td className={`px-3 py-2 text-center ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          {item.cantidad}
                        </td>
                        <td className={`px-3 py-2 text-right ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          {formatCurrency(item.precioUnitario)}
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {formatCurrency(item.subtotal)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className={theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}>
                    <tr>
                      <td colSpan={3} className={`px-3 py-2 text-right font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Total
                      </td>
                      <td className={`px-3 py-2 text-right font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {formatCurrency(pedido.totales.total)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* Historial de estados - versión compacta horizontal */}
          <div className={`rounded-xl shadow-md p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
            <h3 className={`font-semibold mb-3 text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Historial
            </h3>
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-50 dark:bg-gray-700/30">
                <div className={`p-1.5 rounded-md ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <Calendar className={`w-3.5 h-3.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
                </div>
                <div className="flex-1 flex items-center justify-between">
                  <span className={`text-xs font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Pedido creado
                  </span>
                  <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {formatFecha(pedido.fechas.creadoEn)}
                  </span>
                </div>
              </div>
              {pedido.fechas.pagadoEn && (
                <div className="flex items-center gap-3 p-2 rounded-lg bg-green-50 dark:bg-green-900/20">
                  <div className="p-1.5 rounded-md bg-green-200 dark:bg-green-900/40">
                    <CreditCard className="w-3.5 h-3.5 text-green-700 dark:text-green-400" />
                  </div>
                  <div className="flex-1 flex items-center justify-between">
                    <span className={`text-xs font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Pago confirmado
                    </span>
                    <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      {formatFecha(pedido.fechas.pagadoEn)}
                    </span>
                  </div>
                </div>
              )}
              {pedido.fechas.enviadoEn && (
                <div className="flex items-center gap-3 p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                  <div className="p-1.5 rounded-md bg-blue-200 dark:bg-blue-900/40">
                    <Truck className="w-3.5 h-3.5 text-blue-700 dark:text-blue-400" />
                  </div>
                  <div className="flex-1 flex items-center justify-between">
                    <span className={`text-xs font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Pedido enviado
                    </span>
                    <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      {formatFecha(pedido.fechas.enviadoEn)}
                    </span>
                  </div>
                </div>
              )}
              {pedido.fechas.entregadoEn && (
                <div className="flex items-center gap-3 p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                  <div className="p-1.5 rounded-md bg-emerald-200 dark:bg-emerald-900/40">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-700 dark:text-emerald-400" />
                  </div>
                  <div className="flex-1 flex items-center justify-between">
                    <span className={`text-xs font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Pedido entregado
                    </span>
                    <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      {formatFecha(pedido.fechas.entregadoEn)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Columna derecha - Chat (solo para pedidos online) */}
        {pedido.envio.direccion !== 'Venta en local físico' && (
          <div className="xl:h-[calc(100vh-9.5rem)] xl:sticky xl:top-20 overflow-hidden">
            <OrderChat
              pedidoId={pedido.id}
              numeroPedido={pedido.numeroPedido}
              clienteEmail={pedido.cliente.email}
              clienteNombre={pedido.cliente.nombre}
              isAdmin={false}
              className="h-full"
            />
          </div>
        )}
      </div>
    </div>
  );
};
